import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { track } from "../lib/analytics";

const CartContext = createContext(null);
const STORAGE_KEY = "loomara_cart";

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch { return []; }
    });
    const [open, setOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    const add = (product, { size, qty = 1 } = {}) => {
        if (product.inStock === false || product.stockStatus === "out_of_stock") {
            toast("This piece is currently out of stock.");
            return;
        }
        setItems((cur) => {
            const key = `${product.id}::${size || ""}`;
            const idx = cur.findIndex((i) => `${i.productId}::${i.size || ""}` === key);
            if (idx >= 0) {
                const copy = [...cur];
                copy[idx] = { ...copy[idx], qty: copy[idx].qty + qty };
                return copy;
            }
            return [...cur, {
                productId: product.id, slug: product.slug, name: product.name,
                price: product.price, image: product.images?.[0],
                size: size || "", qty,
            }];
        });
        track("add_to_cart", { item_id: product.id, item_name: product.name, price: product.price, item_category: product.category });
        toast("Added to bag ✨");
        setOpen(true);
    };

    const remove = (productId, size) => {
        setItems((cur) => cur.filter((i) => !(i.productId === productId && (i.size || "") === (size || ""))));
    };

    const setQty = (productId, size, qty) => {
        if (qty < 1) return remove(productId, size);
        setItems((cur) => cur.map((i) =>
            (i.productId === productId && (i.size || "") === (size || "")) ? { ...i, qty } : i
        ));
    };

    const clear = () => setItems([]);

    const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
    const count = items.reduce((s, i) => s + i.qty, 0);
    const shipping = subtotal === 0 ? 0 : (subtotal > 1499 ? 0 : 99);
    const total = subtotal + shipping;

    return (
        <CartContext.Provider value={{ items, count, subtotal, shipping, total, add, remove, setQty, clear, open, setOpen }}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    return useContext(CartContext);
}
